import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { ThemeContext } from "../../../contexts/theme";
import { useExchangeRateOptional } from "../../../contexts/exchangeRate";
import { Dropdown, DropdownContent, DropdownItem } from "../../BaseUI/Dropdown";
import Avatar from "../Avatar";
import {
  MdOutlineAccountCircle,
  MdLogout,
  MdOutlineAccountBalance,
  MdOutlineBusiness,
} from "react-icons/md";
import { FiSun, FiMoon, FiRepeat } from "react-icons/fi";
import { translate } from "../../../hooks/translate";
import { hasPageAccess } from "../../../config/roles";
import "./style.scss";

export default function UserDropdown({ hideName }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useContext(ThemeContext);

  const fx = useExchangeRateOptional();
  const rate = fx?.rate ?? null;
  const date = fx?.date ?? null;
  const loading = !!fx?.loading;
  const displayCurrency = fx?.displayCurrency || "USD";
  const fetchRate = fx?.fetchRate;
  const toggleCurrency = fx?.toggleCurrency;

  useEffect(() => {
    if (!fetchRate || rate != null || loading) return;
    fetchRate();
  }, [fetchRate, rate, loading]);

  const canAccessBankAccounts =
    !!user &&
    (user.role?.toLowerCase() === "admin" ||
      hasPageAccess(
        user.role,
        user.modules || [],
        "/account/bank-accounts",
        user.pages || [],
        !!user.fullAccess,
      ));

  const canAccessCompanySettings =
    !!user &&
    (user.role?.toLowerCase() === "admin" ||
      hasPageAccess(
        user.role,
        user.modules || [],
        "/account/company-settings",
        user.pages || [],
        !!user.fullAccess,
      ));

  const signout = () => {
    if (logout()) window.location.href = "login";
  };

  const rateLabel =
    rate != null
      ? `1 USD = ${Number(rate).toFixed(4)} ₺`
      : loading
        ? "Kur yükleniyor…"
        : "USD Kuru";

  const toggleLabel = displayCurrency === "TRY" ? "TL" : "USD";
  const toggleTitle =
    displayCurrency === "TRY"
      ? "Fiyatları USD olarak göster"
      : "Fiyatları TL olarak göster";

  return (
    <div className="user-container">
      <Dropdown
        trigger={
          <Avatar
            src={
              user?.image ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                user?.name || "U"
              )}`
            }
          />
        }
        placement="bottom-end"
        hideDropdownIcon
        triggerClassName="u-pointer"
        className="border-0"
        width="280px"
      >
        <DropdownContent className="user-container__content-dropdown">
          <div className="user-container__infos">
            <Avatar
              src={
                user?.image ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  user?.name || "U"
                )}`
              }
            />
            <div className="user-container__infos__info">
              <span className="user-name">{user?.name || "User"}</span>
              <span className="user-email">{user?.email || "Loading..."}</span>
            </div>
          </div>

          {/* Hızlı Ayarlar: Tema & Döviz Kuru */}
          <div className="user-container__quick-controls">
            {/* Tema Değiştirici */}
            <div
              className="user-container__control-row"
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              role="button"
              tabIndex={0}
            >
              <div className="user-container__control-left">
                <span className="user-container__control-icon">
                  {theme === "light" ? <FiMoon /> : <FiSun />}
                </span>
                <span className="user-container__control-label">
                  {theme === "light" ? "Koyu Tema" : "Açık Tema"}
                </span>
              </div>
              <span className="user-container__pill-tag">
                {theme === "light" ? "Açık" : "Koyu"}
              </span>
            </div>

            {/* Döviz Kuru Kartı */}
            {fx && (
              <div className="user-container__exchange-card">
                <div className="user-container__exchange-header">
                  <span className="exchange-title">Döviz Kuru (TCMB)</span>
                  {date && <span className="exchange-date">{date}</span>}
                </div>
                <div className="user-container__exchange-body">
                  <span className="exchange-rate">{rateLabel}</span>
                  <button
                    type="button"
                    className="exchange-toggle-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCurrency?.();
                    }}
                    disabled={loading}
                    title={toggleTitle}
                    aria-label={toggleTitle}
                  >
                    <FiRepeat size={12} className="exchange-toggle-icon" />
                    <span>{toggleLabel}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="user-container__menu">
            <DropdownItem onClick={() => navigate("/account/profile")}>
              <MdOutlineAccountCircle /> {translate("UI.PROFILE")}
            </DropdownItem>
            {canAccessBankAccounts && (
              <DropdownItem onClick={() => navigate("/account/bank-accounts")}>
                <MdOutlineAccountBalance /> Banka Hesapları
              </DropdownItem>
            )}
            {canAccessCompanySettings && (
              <DropdownItem onClick={() => navigate("/account/company-settings")}>
                <MdOutlineBusiness /> Firma Ayarları
              </DropdownItem>
            )}
            <DropdownItem onClick={signout}>
              <MdLogout /> {translate("UI.LOGOUT")}
            </DropdownItem>
          </div>
        </DropdownContent>
      </Dropdown>
    </div>
  );
}
